import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Button,
  FormControlLabel,
  Switch,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  Box,
} from "@mui/material";
import Color from "../../components/Color";
import { getAllCategoryLists } from "../../service/categoryService";
import { useSnackbar } from "../../App"; // Import useSnackbar hook

const SubCategory = ({ open, onClose, formData, setFormData, onSubmit }) => {
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const { showSnackbar } = useSnackbar(); // Use the snackbar hook

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const categoryRes = await getAllCategoryLists();
        setCategories(
          Array.isArray(categoryRes?.data?.data) ? categoryRes.data.data : []
        );
      } catch (error) {
        showSnackbar("Failed to load categories", "error");
      }
    };
    if (open) {
      fetchDropdowns();
    }
  }, [open, showSnackbar]);

  const validate = () => {
    const newErrors = {};
    if (!formData.category_id) {
      newErrors.category_id = ["Category is required"];
    }
    if (!formData.name?.trim()) {
      newErrors.name = ["Subcategory name is required"];
    } else if (!/^[a-zA-Z ]+$/.test(formData.name)) {
      newErrors.name = ["Subcategory name must contain only letters"];
    }
    if (!formData.description?.trim()) {
      newErrors.description = ["Description is required"];
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validate()) {
      try {
        const response = await onSubmit();
        if (response?.status === "success") {
          showSnackbar(
            formData.id
              ? "Subcategory updated successfully!"
              : "Subcategory created successfully!",
            "success"
          );
          handleCancel();
        } else if (response?.status === "error") {
          if (response.errors) {
            const formattedErrors = {};
            Object.keys(response.errors).forEach((field) => {
              formattedErrors[field] = response.errors[field][0];
            });
            setErrors((prev) => ({ ...prev, ...formattedErrors }));
            showSnackbar(response.message || "Validation failed", "error");
          } else {
            setErrors((prev) => ({
              ...prev,
              form: response.message || "Something went wrong",
            }));
            showSnackbar(response.message || "Something went wrong", "error");
          }
        }
      } catch (err) {
        console.error("Submit failed:", err);
        setErrors((prev) => ({
          ...prev,
          form: "Server error. Please try again later.",
        }));
        showSnackbar("Server error. Please try again later.", "error");
      }
    } else {
      showSnackbar("Please fix validation errors before submitting.", "error");
    }
  };

  const handleCancel = () => {
    setFormData({
      id: null,
      name: "",
      description: "",
      category_id: "",
      is_active: true,
    });
    setErrors({});
    onClose();
  };

  const handleChange = (eOrName, value) => {
    if (typeof eOrName === "string") {
      setFormData((prev) => ({
        ...prev,
        [eOrName]: value,
      }));
      setErrors((prev) => ({
        ...prev,
        [eOrName]: "",
      }));
    } else {
      const { name, value } = eOrName.target;

      if (name === "name") {
        const isValid = /^[a-zA-Z ]*$/.test(value);
        if (!isValid) {
          setErrors((prev) => ({
            ...prev,
            name: "Only letters are allowed",
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            name: "",
          }));
        }
        const filtered = value.replace(/[^a-zA-Z ]/g, "");
        setFormData((prev) => ({
          ...prev,
          [name]: filtered,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
        setErrors((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleCancel();
        }
      }}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          width: "400px",
          maxWidth: "95vw",
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle>
        {formData.id ? "Edit Subcategory" : "Add Subcategory"}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1, sm: 2 },
          maxHeight: { xs: "60vh", sm: "auto" },
          overflowY: "auto",
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 1,
            }}
          >
            {/* Category Dropdown */}
            <Box mb={1}>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: "13px",
                }}
              >
                Category*
              </Typography>
              <FormControl fullWidth error={!!errors.category_id}>
                <Select
                  value={formData.category_id ?? ""}
                  onChange={(e) => handleChange("category_id", e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: "#9e9e9e" }}>Select category</span>;
                    }
                    return (
                      categories.find((cat) => cat.id === selected)?.name || ""
                    );
                  }}
                >
                  <MenuItem value="" disabled>
                    Select category
                  </MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.category_id && (
                  <FormHelperText>{errors.category_id}</FormHelperText>
                )}
              </FormControl>
            </Box>

            {/* Subcategory Name */}
            <Box mb={1}>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: "13px",
                }}
              >
                Subcategory Name*
              </Typography>
              <TextField
                name="name"
                value={formData.name || ""}
                onChange={handleChange}
                placeholder="Enter subcategory name"
                fullWidth
                error={!!errors.name}
                helperText={errors.name}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{ "& .MuiInputLabel-root": { display: "none" } }}
              />
            </Box>

            {/* Description */}
            <Box mb={1}>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: "13px",
                }}
              >
                Description*
              </Typography>
              <TextField
                name="description"
                value={formData.description || ""}
                onChange={handleChange}
                placeholder="Enter description"
                fullWidth
                error={!!errors.description}
                helperText={errors.description}
                multiline
                rows={4}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-inputMultiline": {
                    paddingTop: "8px",
                    paddingBottom: "8px",
                    lineHeight: 1.5,
                  },
                }}
              />
            </Box>
          </Box>

          {/* Active Status */}
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active ?? true}
                  onChange={(e) => handleChange("is_active", e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: Color.primary.main,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: Color.primary.main,
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor: "#ccc",
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    color: formData.is_active
                      ? Color.primary.main
                      : "text.secondary",
                    fontWeight: formData.is_active ? "bold" : "normal",
                  }}
                >
                  {formData.is_active ? "Active" : "Inactive"}
                </Typography>
              }
            />
          </Box>

          {/* Backend Error Message */}
          {errors.form && (
            <Typography color="error" variant="body2" mt={1}>
              {errors.form}
            </Typography>
          )}

          {/* Submit and Cancel Buttons */}
          <Box display="flex" justifyContent="flex-end" gap={2} mt={1} mb={1}>
            <Button
              onClick={handleCancel}
              sx={{
                background: "none",
                color: Color.secondary.main,
                border: "1px solid",
                borderColor: Color.secondary.main,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              sx={{ background: Color.primary.main, color: "white" }}
            >
              {formData.id ? "Update" : "Submit"}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SubCategory;